import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout/Layout";
import axios from "axios";
import { useSelector } from "react-redux";
import "./Dashboard.css";
import "./Orders.css";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "./components/DashboardLayout";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";

const Orders = () => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const getAllUserOrders = async () => {
    if (!user?.email) return;

    try {
      setLoading(true);
      const res = await axios.post(
        "/api/order/get-user-orders",
        { email: user.email },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );

      if (res.data?.success) {
        const arr = Array.isArray(res.data.data) ? res.data.data : [];
        arr.sort(
          (a, b) =>
            new Date(b?.orderDate || 0).getTime() -
            new Date(a?.orderDate || 0).getTime()
        );
        setAllOrders(arr);
      } else {
        setAllOrders([]);
      }
    } catch (err) {
      console.error(err);
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) getAllUserOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const recentOrders = useMemo(() => allOrders.slice(0, 10), [allOrders]);

  const formatCurrency = (value, quantity) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "-";

    const text = `\u20b9${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;

    return quantity > 1 ? `${text} (x${quantity})` : text;
  };

  const formatDateTime = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleString("default", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const userName = String(user?.fname || "").trim() || "User";
  const userEmail = String(user?.email || "").trim();

  const getOrderType = (item) => (item?._type === "explore" ? "service" : "order");
  const getUserId = (item) => (item?._type === "explore" ? item?.in_game_user_id : item?.userId) || "-";
  const getZoneId = (item) => (item?._type === "explore" ? item?.in_game_zone_id : item?.zoneId) || "-";
  const getStatus = (item) => String(item?.status || "pending").toLowerCase();

  const renderRow = (item, index) => {
    const orderType = getOrderType(item);
    const status = getStatus(item);

    return (
      <tr key={item?.orderId || index}>
        <td className="mono">
          #{item?.orderId || "-"}
          <span className={`orderTypeBadge ${orderType === "service" ? "explore" : ""}`}>
            {orderType}
          </span>
        </td>
        <td>{item?.pack || "-"}</td>
        <td className="mono">{getUserId(item)}</td>
        <td className="mono">{getZoneId(item)}</td>
        <td>{formatDateTime(item?.orderDate)}</td>
        <td>
          <span className={`statusPill ${status}`}>{item?.status || "Pending"}</span>
        </td>
        <td className="mono">{formatCurrency(item?.amount, item?.quantity)}</td>
        <td>{item?.product_name || "-"}</td>
      </tr>
    );
  };

  const renderMobileCard = (item, index) => {
    const orderType = getOrderType(item);
    const status = getStatus(item);

    return (
      <article className="orderMobileCard" key={item?.orderId || index}>
        <div className="orderMobileTop">
          <div>
            <span className="orderMobileLabel">{orderType}</span>
            <strong>{formatCurrency(item?.amount, item?.quantity)}</strong>
          </div>

          <span className={`statusPill ${status}`}>{item?.status || "Pending"}</span>
        </div>

        <div className="orderMobileProduct">
          <span>{item?.product_name || "Product"}</span>
          <small>{item?.pack || "Pack not available"}</small>
        </div>

        <div className="orderMobileGrid">
          <div>
            <small>Order ID</small>
            <b className="mono">#{item?.orderId || "-"}</b>
          </div>
          <div>
            <small>User ID</small>
            <b className="mono">{getUserId(item)}</b>
          </div>
          <div>
            <small>Zone ID</small>
            <b className="mono">{getZoneId(item)}</b>
          </div>
          <div>
            <small>Date</small>
            <b>{formatDateTime(item?.orderDate)}</b>
          </div>
        </div>
      </article>
    );
  };

  return (
    <Layout>
      <DashboardLayout>
        <section className="ordersOverview">
          <div className="ordersOverviewCopy">
            <span className="ordersEyebrow">Orders</span>
            <h2>Hello, {userName}</h2>
            <p>Track all your top-up and service orders in one place.</p>
            {userEmail && <small>{userEmail}</small>}
          </div>

          <div className="ordersCountCard">
            <span className="ordersCountIcon">
              <InstallMobileIcon />
            </span>
            <strong>{loading ? "..." : allOrders.length}</strong>
            <span>Total Orders</span>
          </div>
        </section>

        <section className="dashSection ordersSection">
          <div className="dashSectionHeader">
            <div>
              <h3 className="dashTitle">Recent Orders</h3>
              <p className="ordersSectionSub">
                Showing your latest {Math.min(recentOrders.length, 10)} orders.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="loaderWrap">
              <span className="loader" />
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="noOrderDash">
              <b>No Order Found</b>
              <button className="dashLinkBtn" type="button" onClick={() => navigate("/")}>
                Go to Shop
              </button>
            </div>
          ) : (
            <>
              <div className="ordersMobileList">
                {recentOrders.map(renderMobileCard)}
              </div>

              <div className="dashTableWrap ordersTableWrap">
                <table className="dashTable ordersTable">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Pack / Variant</th>
                      <th>User ID</th>
                      <th>Zone ID</th>
                      <th>Order Date</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Product Name</th>
                    </tr>
                  </thead>
                  <tbody>{recentOrders.map(renderRow)}</tbody>
                </table>
              </div>

              {allOrders.length > recentOrders.length && (
                <div className="dashHint">
                  Showing latest {recentOrders.length} of {allOrders.length} orders.
                </div>
              )}
            </>
          )}
        </section>
      </DashboardLayout>
    </Layout>
  );
};

export default Orders;
